import * as React from 'react';
import Link from 'next/link';
import styles from './link-blocks.module.scss';
import type { LinkTarget, UnitHubBlocks, UnitPairBlocks } from '../utils/link-hierarchy';

const APP = '/unit-converter';
/** In-app targets go through next/link (basePath-relative); FP / time-converter targets are plain root-relative anchors. */
function Anchor({ t, className }: { t: LinkTarget; className?: string }) {
    if (t.href === APP || t.href.startsWith(APP + '/')) {
        return <Link href={t.href === APP ? '/' : t.href.slice(APP.length)} className={className}>{t.text}</Link>;
    }
    return <a href={t.href} className={className}>{t.text}</a>;
}

/**
 * "File tools" row (6 fixed FP-T1 + rotating FP-T1/T2) and the "More converters" slot (rotating TIME-HV) — METHOD §4,
 * rendered from pageProps.linkBlocks on every unit page (pair, hub, root). Static server HTML: no layout shift.
 */
export function LinkRows({ blocks }: { blocks?: Pick<UnitPairBlocks, 'fileTools' | 'moreConverters'> | UnitHubBlocks | null }) {
    if (!blocks || !blocks.fileTools?.length) return null;
    return (
        <nav className={styles['link-rows']} aria-label="More XConvert tools">
            <div className={styles['link-row']} data-block="file-tools">
                <span className={styles['link-row-label']}>File tools</span>
                {blocks.fileTools.map(t => <Anchor key={t.href} t={t} className={styles['link-chip']} />)}
            </div>
            {blocks.moreConverters?.length > 0 && (
                <div className={styles['link-row']} data-block="more-converters">
                    <span className={styles['link-row-label']}>More converters</span>
                    {blocks.moreConverters.map(t => <Anchor key={t.href} t={t} className={styles['link-chip']} />)}
                </div>
            )}
        </nav>
    );
}

/** "Popular {measure} conversions": same-measure UNIT-HV first, then rotation over all UNIT-HV (anchor = target H1). */
export function PopularUnitBlock({ measureName, links }: { measureName: string; links: LinkTarget[] }) {
    if (!links?.length) return null;
    return (
        <div className={styles.popular} data-block="popular-unit">
            <h3>Popular {measureName} conversions</h3>
            <ul>
                {links.map(t => <li key={t.href}><Anchor t={t} /></li>)}
            </ul>
        </div>
    );
}

/** Nearest neighbours: X->Y' for the units adjacent to Y (+ X->base unit) — the only LV->LV links an LV page carries. */
export function NeighboursBlock({ links }: { links: LinkTarget[] }) {
    if (!links?.length) return null;
    return (
        <div className={styles.nearby} data-block="neighbours">
            <span className={styles['nearby-label']}>Nearby:</span>
            {links.map(t => <Anchor key={t.href} t={t} className={styles['nearby-link']} />)}
        </div>
    );
}
